import {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  Ref,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';

import debounce from 'debounce';

import { useOutsideClickDetection } from '@hooks/use-outside-click-detection';

export type Suggestion<Data = undefined> = {
  id: string;
  firstRow: string;
  secondRow: string;
  data: Data;
};

type UseSuggestionsOutput<Data = undefined> = {
  inputValue: string;
  highlightedIndex: number;
  suggestions: Suggestion<Data>[];
  wrapperRef: Ref<HTMLDivElement>;
  inputRef: Ref<HTMLInputElement>;
  onSuggestionMouseEnter: (suggestion: Suggestion<Data>) => void;
  onSuggestionClick: (suggestion: Suggestion<Data>) => void;
  onInputKeydown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onInputFocus: (e: ChangeEvent<HTMLInputElement>) => void;
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  clear: () => void;
  listClosed: boolean;
  loading: boolean;
  requestFailed: boolean;
  setListClosed: Dispatch<SetStateAction<boolean>>;
  setInputValue: Dispatch<SetStateAction<string>>;
};

export type UseSuggestionsParams<Data = undefined> = {
  defaultInputValue?: string;
  debounceInMs: number;
  onSelect?: (suggestion: Suggestion<Data>) => void;
  onInputChange?: (value: string) => void;
  fetchSuggestions: (query: string) => Promise<Suggestion<Data>[]>;
  setInputValue?: (
    suggestion: Suggestion<Data>,
  ) => Promise<string | number> | string | number;
};

export const useSuggestions = <Data = undefined>({
  defaultInputValue = '',
  onSelect,
  debounceInMs,
  fetchSuggestions,
  onInputChange: onInputChangeCallback,
  setInputValue: customSetInputValue,
}: UseSuggestionsParams<Data>): UseSuggestionsOutput<Data> => {
  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [suggestions, setSuggestions] = useState<Suggestion<Data>[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [listClosed, setListClosed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fetchedFor = useRef<string>();

  const sendRequest = useCallback(
    async (val: string) => {
      if (fetchedFor.current === val) return;

      setLoading(true);
      setRequestFailed(false);

      try {
        setSuggestions([]);
        const res = await fetchSuggestions(val);
        fetchedFor.current = val;
        setSuggestions(res);
      } catch (e) {
        setRequestFailed(true);
      }

      setLoading(false);
    },
    [fetchSuggestions],
  );

  const onSuggestionClick = useCallback(
    async (suggestion: Suggestion<Data>) => {
      const value = customSetInputValue
        ? (await customSetInputValue(suggestion)).toString()
        : suggestion['firstRow'];
      setInputValue(value);
      await sendRequest(suggestion.firstRow);
      setListClosed(true);
      if (onSelect) onSelect(suggestion);
    },
    [onSelect],
  );

  const onSuggestionMouseEnter = useCallback(
    (suggestion: Suggestion<Data>) => {
      setHighlightedIndex(
        suggestions.findIndex(
          (suggestion2) => suggestion.id === suggestion2.id,
        ),
      );
    },
    [suggestions],
  );

  const onInputKeydown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (['Tab', 'Escape'].includes(e.code)) setListClosed(true);

      if (!suggestions?.length || listClosed) return;

      const { key } = e;

      if (key === 'ArrowDown') {
        e.preventDefault();
        const index = (highlightedIndex + 1) % suggestions.length;
        setHighlightedIndex(index);
        setInputValue(suggestions[index].firstRow);
      }

      if (key === 'ArrowUp') {
        e.preventDefault();
        const index = Math.max(-1, highlightedIndex - 1);
        setHighlightedIndex(index);
        if (index > -1) setInputValue(suggestions[index].firstRow);
      }

      if (key === 'Enter') {
        if (highlightedIndex > -1) {
          const suggestion = suggestions[highlightedIndex];
          if (suggestion) {
            if (onSelect) onSelect(suggestion);
            setInputValue(suggestion.firstRow);
            setListClosed(true);
            inputRef.current?.blur();
          }
        }
      }
    },
    [onSelect, suggestions, highlightedIndex, listClosed],
  );

  const onSearchDebounced = useCallback(
    debounce(sendRequest, debounceInMs),
    [],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setListClosed(false);
      onSearchDebounced(newValue);
      if (onInputChangeCallback) onInputChangeCallback(newValue);
    },
    [onSearchDebounced],
  );

  const onInputFocus = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length) setListClosed(false);
    await onSearchDebounced(e.target.value);
  }, []);

  const clear = useCallback(() => {
    setInputValue('');
    setSuggestions([]);
    setListClosed(true);
  }, []);

  useOutsideClickDetection(wrapperRef, () => setListClosed(true));

  const output: UseSuggestionsOutput<Data> = {
    inputValue,
    highlightedIndex,
    suggestions,
    listClosed,
    loading,
    wrapperRef,
    inputRef,
    requestFailed,
    setListClosed,
    onSuggestionClick,
    onSuggestionMouseEnter,
    onInputChange,
    onInputFocus,
    onInputKeydown,
    clear,
    setInputValue,
  };

  return output;
};
